<template>
	<div id="initial-decision" v-if="!hideInitialDecisions">
		<h3 class="line-header">Initial Decision</h3>
		<div class="initial-decision-content">
			<p>
				Initial decision checks are a way to avoid unnecessary processing
				for specific situations. These checks are performed immediately and
				in order from top to bottom. If any condition is met, the claim will
				route to the given decision without running further evaluation.
			</p>
			<v-form v-model="initialDecisionsValid">
				<draggable
					handle=".handle"
					v-model="initialDecisionItems"
					@end="updatePriorities()"
					:animation="200"
					ghostClass="ghost"
				>
					<div
						class="initial-decision-action-bar"
						v-for="(item, priority) of initialDecisionItems"
						:key="priority"
					>
						<v-icon
							class="handle initial-decision-row-action"
							v-if="isModifiable"
							title="move"
							label="move"
						>
							mdi-drag-horizontal-variant
						</v-icon>
						<v-text-field
							v-if="isModifiable"
							v-model="item.priority"
							min="1"
							:max="initialDecisionItems.length"
							:disabled="!isModifiable"
							@change="changePriorityDirectly(item)"
							:rules="[validationPriorities(item)]"
							ref="item"
							class="initial-decision-action-bar-priority"
							required
						></v-text-field>
						<div class="initial-decision-action-bar-content">
							<div class="initial-decision-type">
								<div class="initial-decision-action-bar-text">When</div>
								<div class="initial-decision-action-bar-text">
									{{ item.label }}
								</div>
							</div>
							<div class="initial-decision-condition">
								<div
									class="initial-decision-action-bar-text"
									v-show="
										item.operatorLabel !== 'Has Value' &&
										item.operatorLabel !== 'Has No Value'
									"
								>
									is
								</div>
								<v-select
									:items="item.operatorOptionsLabels"
									v-model="item.operatorLabel"
									placeholder="select operator"
									editable
									item-value="text"
									class="initial-decision-action-bar-select initial-decision-action-bar-operation"
									outlined
									:disabled="!isModifiable"
									@change="setProfileChanged"
								></v-select>
								<v-select
									v-show="
										item.operatorLabel !== 'Has Value' &&
										item.operatorLabel !== 'Has No Value'
									"
									:items="item.rightSideOptionsLabels"
									v-model="item.rightSideSelectedOption"
									placeholder="select operator"
									editable
									item-value="text"
									class="initial-decision-action-bar-select"
									outlined
									:disabled="!isModifiable"
									@change="setProfileChanged"
								></v-select>
								<v-text-field
									v-if="
										item.rightSideSelectedOption === 'Value' &&
										item.operatorLabel !== 'Has Value' &&
										item.operatorLabel !== 'Has No Value'
									"
									:placeholder="item.placeholder"
									:label="item.label"
									:style="'width: ' + ( (item.placeholder.length / 2) + 3 ) + 'em'"
									v-model="item.value"
									:type="
										setType(
											item.rightSideOptions.find(
												(op) => op.optionId === item.rightSideOptionId
											).dataType
										)
									"
									:rules="[
										validateField(
											item.value,
											item.rightSideOptions.find(
												(op) => op.optionId === item.rightSideOptionId
											).validateRegex
										),
									]"
									outlined
									:disabled="!isModifiable"
									@change="setProfileChanged"
									required
									class="initial-decision-value"
								></v-text-field>
							</div>
							<div class="initial-decision-outcome">
								<div class="initial-decision-action-bar-text">then</div>
								<v-select
									:items="initialDecisionOutcomeListLabels"
									v-model="item.decisionLabel"
									placeholder="selected decision"
									editable
									item-value="text"
									class="initial-decision-action-bar-select initial-decision-action-bar-decision"
									outlined
									:disabled="!isModifiable"
									@change="setProfileChanged"
								></v-select>
							</div>
						</div>
						<v-menu
							v-if="isModifiable"
							offset-y
						>
							<template v-slot:activator="{ on, attrs }">
								<v-btn
									text
									v-bind="attrs"
									v-on="on"
									aria-label="menu"
									title="menu"
									class="initial-decision-action-bar-menu-btn initial-decision-row-action"
									:disabled="!isModifiable"
								>
									<v-icon>mdi-dots-vertical</v-icon>
								</v-btn>
							</template>
							<v-btn class="dangerous" @click="deleteDecision(item)"
								>Delete</v-btn
							>
						</v-menu>
					</div>
				</draggable>
			</v-form>

			<div class="initial-decision-add-decision-button">
				<template v-if="isModifiable">
					<modal-dialog
						title="Add a Decision"
						executeText="Add Decision"
						:executeAction="addDecision.bind(this, ADL[radioGroup])"
					>
						<div slot="activator">
							<v-btn text>
								<v-icon left>mdi-plus</v-icon>
								Add a Decision
							</v-btn>
						</div>
						<div slot="content" >
							<v-radio-group v-model="radioGroup">
								<v-radio v-for="(config, index) in ADL" :key="index" :label="config.label" class="decision-radio-buttons"></v-radio>
							</v-radio-group>
						</div>
					</modal-dialog>
				</template>
			</div>
		</div>
	</div>
</template>

<script>
	/**
	 * @fileoverview initial-decision.vue displays the profile's initial decisions in a draggable UI. 
	 * It relies on vuex to map the state variables from the store files and vuedraggable for the drag and drop features.
	 * 
	 * @author Sophia Frankel <sophia.frankel@quavo.com>
	 */
	import { mapState } from "vuex";

	export default {
		data() {
			return {
				radioGroup: 0,	// radio button identifier for adding a new initial decision
				initialDecisionsValid: false,	// initial decision validation flag
			};
		},
		props: {
			/**
			 * Modifiable flag
			 */
			isModifiable: {
				required: true,
			},
		},
		computed: {
			...mapState({
				initialDecisionOutcomeList: (state) =>
					state.config.initialDecisionOutcomeList,
				initialDecisionOutcomeListLabels: (state) =>
					state.config.initialDecisionOutcomeListLabels,
				ADL: (state) => state.config.availableDecisionsList,
				hideInitialDecisions: (state) => state.config.hideInitialDecisions
			}),
			/**
			 * Getter and setter for the initial decision items
			 */
			initialDecisionItems: {
				get() {
					return this.$store.state.config.initialDecisionItems;
				},
				set(value) {
					this.$store.dispatch('setInitialDecisionItems', value);
				}
			}
		},
		watch: {
			/**
			 * Set initialDecisionsValid in the config store
			 */
			initialDecisionsValid() {
				this.$store.dispatch(
					"setInitialDecisionsValid",
					this.initialDecisionsValid
				);
			},
		},
		methods: {
			/**
			 * Change the priority of an initial decision using the priority field
			 * @param {Object} item - initial decision object
			 */
			changePriorityDirectly(item) {
				if (
					item.priority !== undefined &&
					item.priority > 0 &&
					item.priority < this.initialDecisionItems.length + 1
				) {
					const index = this.initialDecisionItems.findIndex(
						(decision) =>
							decision.initialDecisionConfigId ===
							item.initialDecisionConfigId
					);

					this.initialDecisionItems.splice(index, 1);
					this.initialDecisionItems.splice(item.priority - 1, 0, item);

					this.$refs.item[index].blur();

					this.updatePriorities();
				}
			},
			/**
			 * Update the initial decision priorities based on their index
			 */
			updatePriorities() {
				this.initialDecisionItems.map((item, index) => {
					item.priority = index + 1;
				});

				this.setProfileChanged();
			},
			/**
			 * Priority validation
			 * @param {Object} item - initial decision object
			 * @returns true if valid, and an invalid string in invalid
			 */
			validationPriorities(item) {
				if (
					item.priority !== undefined &&
					item.priority > 0 &&
					item.priority < this.initialDecisionItems.length + 1
				) {
					return true;
				} else {
					return "Invalid Input";
				}
			},
			/**
			 * Remove initial decision from the initialDecisionItems
			 * @param {Obejct} decision - initial decision object
			 */
			deleteDecision(decision) {
				// remove from initialDecisionItems
				var index = this.initialDecisionItems.findIndex(
					(dec) =>
						dec.initialDecisionConfigId ===
						decision.initialDecisionConfigId
				);

				if (index !== undefined) {
					this.initialDecisionItems.splice(index, 1);
				}

				this.updatePriorities();
				this.$store.dispatch("createAvialableDecisionsList");
			},
			/**
			 * Add initial decision to the initialDecisionItems
			 * @param {Obejct} decision - initial decision object
			 */
			addDecision(decision) {
				// add decision to initialDecisionItems
				var newDecision = {
					priority: this.initialDecisionItems.length + 1,
					label: decision.label,
					initialDecisionConfigId: decision.initialDecisionConfigId,
					value: decision.rightSideOptions[0].value,
					placeholder: (decision.rightSideOptions[0].placeholder !== undefined ? decision.rightSideOptions[0].placeholder : ''),
					operator: decision.operatorOptions[0],
					operatorLabel: decision.operatorOptions[0].label,
					decision: this.initialDecisionOutcomeList[0]
						.initialDecisionOutcomeId,
					decisionLabel: this.initialDecisionOutcomeList[0].label,
					operatorOptionsLabels: decision.operatorOptions.map((op) => {
						return op.label;
					}),
					operatorOptions: decision.operatorOptions,
					rightSideSelectedOption: decision.rightSideOptions[0].label,
					rightSideOptionId: decision.rightSideOptions[0].optionId,
					rightSideOptionsLabels: decision.rightSideOptions.map((dec) => {
						return dec.label;
					}),
					rightSideOptions: decision.rightSideOptions,
					isActive: decision.isActive,
					multipleEntry: decision.multipleEntry
				};

				this.initialDecisionItems.push(newDecision);
				this.$store.dispatch("createAvialableDecisionsList");
				this.setProfileChanged();
			},
			/**
			 * Compare field against its regex pattern
			 * @param {*} value - value to validate. Can be multiple types.
			 * @param {String} srcRegex - regex string for validation
			 * @returns true if valid, and an invalid message if invalid
			 */
			validateField(value, srcRegex) {
				/*
				 * TODO: consider presets that can't be done as easily via regex, i.e.
				 *	futureDate, pastDate ... assuming we set the right HTML5 type, there are
				 *	attributes we can set for many checks/controls and we can feed those in
				 * 	without/in addition to this method
				 */

				/*
				 * check the given value against the given regex pattern
				 * a match returns true and means the input is valid
				 */

				return function () {
					let success = true;
					let message = undefined;

					if (!value) {
						success = false;
						message = "Incomplete or Invalid";
					} else if (srcRegex) {
						const regex = RegExp(srcRegex);
						success = regex.test(value);
						if (!success) {
							message = "Invalid Response";
						}
					}
					return message ? message : success;
				};
			},
			/**
			 * Set the profile changed flag to true in the config store
			 */
			setProfileChanged() {
				this.$store.dispatch("setProfileChanged", true);
			},
			/**
			 * Set the field type
			 * @param {String} srcType - original type
			 */
			setType(srcType) {
				/*
				 *	TODO: see if we can just enforce during creation/entry...
				 *
				 *	https://developer.mozilla.org/en-US/docs/Learn/Forms/HTML5_input_types
				 *		text, email, search, tel, url, number, range, color
				 *		date, datetime-local, month, time, week
				 */
				let type = srcType ? srcType.toLowerCase() : "text";

				switch (type) {
					case "integer":
					case "long":
					case "decimal":
					case "number":
						type = "number";
						break;
					case "phone":
						type = "tel";
						break;
					default:
						break;
				}

				return type;
			},
		},
	};
</script>

<!-- Styles defined in _initial-decision.scss -->